
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { getPlanFeatures, isUnlimited } from '@/constants/plans';
import { debugLogger } from '@/utils/debugLogger';
import { useProfile } from '@/contexts/ProfileContext';

interface LikeLimits {
  current: number;
  limit: number;
  remaining: number;
  canLike: boolean;
  isUnlimited: boolean;
}

export const useLikeLimits = () => {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile(); // Usar ProfileContext
  const [likeLimits, setLikeLimits] = useState<LikeLimits>({
    current: 0,
    limit: 5,
    remaining: 5,
    canLike: true,
    isUnlimited: false
  });
  const [loading, setLoading] = useState(true);

  const checkLikeLimits = useCallback(async () => {
    if (!user?.id || !userProfile) return;

    try {
      setLoading(true);

      const planType = userProfile.plan_type || 'sponsor_free';
      const features = getPlanFeatures(planType);
      const dailyLimit = features.likes_daily;

      debugLogger.debug('LikeLimits', 'User profile data:', { 
        plan_type: planType, 
        role: userProfile.role,
        dailyLimit
      });

      // Count today's likes using RPC (Phase 7: no UUID exposed)
      const { data: todayCount, error: likesError } = await supabase
        .rpc('get_today_connections_count');

      if (likesError) {
        debugLogger.error('LikeLimits', 'Error counting today likes:', likesError);
        return;
      }

      const currentCount = todayCount || 0;
      const unlimited = isUnlimited(dailyLimit);
      const remaining = unlimited ? -1 : Math.max(0, dailyLimit - currentCount);
      const canLike = unlimited || remaining > 0;

      setLikeLimits({
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canLike,
        isUnlimited: unlimited
      });

      debugLogger.debug('LikeLimits', 'Like limits updated:', {
        current: currentCount,
        limit: dailyLimit,
        remaining,
        canLike,
        isUnlimited: unlimited
      });

    } catch (error) {
      debugLogger.error('LikeLimits', 'Error checking like limits:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.id, userProfile]);

  const incrementLikeCount = useCallback(() => {
    setLikeLimits(prev => {
      if (prev.isUnlimited) return prev;
      
      const newCurrent = prev.current + 1;
      const newRemaining = Math.max(0, prev.limit - newCurrent);
      
      return {
        ...prev,
        current: newCurrent,
        remaining: newRemaining,
        canLike: newRemaining > 0
      };
    });
  }, []);

  useEffect(() => {
    checkLikeLimits();
  }, [checkLikeLimits]);

  return {
    likeLimits,
    loading,
    checkLikeLimits,
    incrementLikeCount
  };
};
